import React, { useState } from 'react';
import { Calendar, Clock, MapPin, Euro, FileText, CheckCircle, Music, Briefcase, Shield, Send, Edit3, Download, Share2 } from 'lucide-react';
import Button from '../../../components/ui/Button';

const BookingIntegration = ({
  selectedConversation,
  currentUser,
  userSubscription,
  onCreateContract
}) => {
  const [activeTab, setActiveTab] = useState('negotiate');
  const [bookingDetails, setBookingDetails] = useState({
    event_type: '',
    date: '',
    time: '',
    duration: '',
    venue: '',
    audience_size: '',
    budget_min: '',
    budget_max: '',
    requirements: '',
    contract_type: 'standard'
  });
  const [proposalStatus, setProposalStatus] = useState('draft'); // draft, sent, negotiating, accepted, declined
  const [contractTerms, setContractTerms] = useState({
    performance_fee: '',
    technical_requirements: '',
    cancellation_policy: '48h',
    payment_terms: '50% advance',
    travel_expenses: false,
    equipment_provided: false
  });
  const [negotiationHistory, setNegotiationHistory] = useState([
    {
      id: 1,
      timestamp: new Date(Date.now() - 2 * 60 * 60 * 1000),
      from: 'organizer',
      type: 'initial_offer',
      details: {
        budget: '2500€',
        date: '2025-10-15',
        venue: 'Salle Olympia Privée'
      },
      message: 'Proposition initiale pour un concert privé'
    },
    {
      id: 2,
      timestamp: new Date(Date.now() - 1 * 60 * 60 * 1000),
      from: 'artist',
      type: 'counter_offer',
      details: {
        budget: '3000€',
        technical_requirements: 'Son et éclairage professionnel inclus'
      },
      message: 'Contre-proposition avec exigences techniques'
    }
  ]);

  const tabs = [
    { id: 'negotiate', label: 'Négociation', icon: 'MessageSquare' },
    { id: 'contract', label: 'Contrat', icon: 'FileText' },
    { id: 'calendar', label: 'Planning', icon: 'Calendar' },
    { id: 'payment', label: 'Paiement', icon: 'Euro' }
  ];

  const eventTypes = [
    { id: 'concert', label: 'Concert', description: 'Performance live standard' },
    { id: 'private', label: 'Événement privé', description: 'Soirée ou réception privée' },
    { id: 'festival', label: 'Festival', description: 'Participation à un festival' },
    { id: 'corporate', label: 'Événement d\'entreprise', description: 'Prestation corporate' },
    { id: 'wedding', label: 'Mariage', description: 'Animation de mariage' }
  ];

  const handleBookingSubmit = () => {
    const proposal = {
      id: Date.now(),
      timestamp: new Date(),
      from: currentUser?.partners?.[0]?.partner_type === 'artist' ? 'artist' : 'organizer',
      type: 'booking_proposal',
      details: bookingDetails,
      message: 'Nouvelle proposition de booking'
    };

    setNegotiationHistory(prev => [...prev, proposal]);
    setProposalStatus('sent');
  };

  const formatPrice = (amount) => {
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: 'EUR'
    })?.format(amount);
  };

  const formatDate = (date) => {
    return new Intl.DateTimeFormat('fr-FR', {
      weekday: 'long',
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    })?.format(new Date(date));
  };

  const renderNegotiationTab = () => (
    <div className="space-y-6">
      {/* Current Status */}
      <div className={`rounded-lg p-4 ${
        proposalStatus === 'accepted' ? 'bg-green-50 border border-green-200' :
        proposalStatus === 'negotiating' ? 'bg-yellow-50 border border-yellow-200' :
        proposalStatus === 'sent'? 'bg-blue-50 border border-blue-200' : 'bg-gray-50 border border-gray-200'
      }`}>
        <div className="flex items-center space-x-3">
          <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
            proposalStatus === 'accepted' ? 'bg-green-500' :
            proposalStatus === 'negotiating' ? 'bg-yellow-500' :
            proposalStatus === 'sent'? 'bg-blue-500' : 'bg-gray-500'
          }`}>
            {proposalStatus === 'accepted' ? <CheckCircle className="w-5 h-5 text-white" /> :
             proposalStatus === 'negotiating' ? <Clock className="w-5 h-5 text-white" /> :
             <Send className="w-5 h-5 text-white" />}
          </div>
          <div>
            <h3 className="font-semibold text-gray-900">
              {proposalStatus === 'accepted' ? 'Booking Confirmé' :
               proposalStatus === 'negotiating' ? 'Négociation en Cours' :
               proposalStatus === 'sent'? 'Proposition Envoyée' : 'Nouvelle Négociation'}
            </h3>
            <p className="text-sm text-gray-600">
              {proposalStatus === 'accepted' ? 'Le contrat peut être finalisé' :
               proposalStatus === 'negotiating' ? 'En attente de réponse' :
               proposalStatus === 'sent'? 'En attente d\'acceptation' : 'Créez votre proposition de booking'}
            </p>
          </div>
        </div>
      </div>

      {/* Booking Form */}
      <div className="bg-white border border-gray-200 rounded-lg p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">
          Détails de la Prestation
        </h3>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Type d'événement
            </label>
            <select
              value={bookingDetails?.event_type}
              onChange={(e) => setBookingDetails(prev => ({ ...prev, event_type: e?.target?.value }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="">Sélectionner un type</option>
              {eventTypes?.map((type) => (
                <option key={type?.id} value={type?.id}>
                  {type?.label} - {type?.description}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Date de l'événement
            </label>
            <input
              type="date"
              value={bookingDetails?.date}
              onChange={(e) => setBookingDetails(prev => ({ ...prev, date: e?.target?.value }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Heure de début
            </label>
            <input
              type="time"
              value={bookingDetails?.time}
              onChange={(e) => setBookingDetails(prev => ({ ...prev, time: e?.target?.value }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Durée (minutes)
            </label>
            <input
              type="number"
              value={bookingDetails?.duration}
              onChange={(e) => setBookingDetails(prev => ({ ...prev, duration: e?.target?.value }))}
              placeholder="90"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Lieu
            </label>
            <input
              type="text"
              value={bookingDetails?.venue}
              onChange={(e) => setBookingDetails(prev => ({ ...prev, venue: e?.target?.value }))}
              placeholder="Nom du lieu et adresse"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Taille du public
            </label>
            <input
              type="number"
              value={bookingDetails?.audience_size}
              onChange={(e) => setBookingDetails(prev => ({ ...prev, audience_size: e?.target?.value }))}
              placeholder="200"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Budget minimum (€)
            </label>
            <input
              type="number"
              value={bookingDetails?.budget_min}
              onChange={(e) => setBookingDetails(prev => ({ ...prev, budget_min: e?.target?.value }))}
              placeholder="2000"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Budget maximum (€)
            </label>
            <input
              type="number"
              value={bookingDetails?.budget_max}
              onChange={(e) => setBookingDetails(prev => ({ ...prev, budget_max: e?.target?.value }))}
              placeholder="3000"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
        </div>

        <div className="mt-4">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Exigences particulières
          </label>
          <textarea
            value={bookingDetails?.requirements}
            onChange={(e) => setBookingDetails(prev => ({ ...prev, requirements: e?.target?.value }))}
            rows={3}
            placeholder="Décrivez vos exigences techniques, logistiques ou artistiques..."
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>

        <div className="flex justify-end mt-6">
          <Button onClick={handleBookingSubmit}>
            <Send className="w-4 h-4 mr-2" />
            Envoyer la Proposition
          </Button>
        </div>
      </div>

      {/* Negotiation History */}
      <div className="bg-white border border-gray-200 rounded-lg p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">
          Historique des Négociations
        </h3>
        
        <div className="space-y-4">
          {negotiationHistory?.map((item) => (
            <div key={item?.id} className={`border rounded-lg p-4 ${
              item?.from === 'artist' ? 'border-blue-200 bg-blue-50' : 'border-green-200 bg-green-50'
            }`}>
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-center space-x-2">
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                    item?.from === 'artist' ? 'bg-blue-500' : 'bg-green-500'
                  }`}>
                    {item?.from === 'artist' ? <Music className="w-4 h-4 text-white" /> : <Briefcase className="w-4 h-4 text-white" />}
                  </div>
                  <span className="font-medium text-gray-900">
                    {item?.from === 'artist' ? 'Artiste' : 'Organisateur'}
                  </span>
                </div>
                <span className="text-sm text-gray-500">
                  {item?.timestamp?.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}
                </span>
              </div>
              
              <p className="text-sm text-gray-700 mb-3">{item?.message}</p>
              
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                {item?.details?.budget && (
                  <div className="flex items-center space-x-2">
                    <Euro className="w-4 h-4 text-gray-400" />
                    <span className="text-sm font-medium">{item?.details?.budget}</span>
                  </div>
                )}
                {item?.details?.date && (
                  <div className="flex items-center space-x-2">
                    <Calendar className="w-4 h-4 text-gray-400" />
                    <span className="text-sm">{formatDate(item?.details?.date)}</span>
                  </div>
                )}
                {item?.details?.venue && (
                  <div className="flex items-center space-x-2">
                    <MapPin className="w-4 h-4 text-gray-400" />
                    <span className="text-sm">{item?.details?.venue}</span>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  const renderContractTab = () => (
    <div className="space-y-6">
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div className="flex items-start space-x-3">
          <Shield className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
          <div>
            <h3 className="font-semibold text-blue-900 mb-1">
              Contrat Sécurisé avec Signature Électronique
            </h3>
            <p className="text-sm text-blue-800">
              Vos contrats sont protégés par chiffrement et juridiquement valides avec signature électronique légale.
            </p>
          </div>
        </div>
      </div>

      <div className="bg-white border border-gray-200 rounded-lg p-6">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-lg font-semibold text-gray-900">
            Termes du Contrat
          </h3>
          <div className="flex space-x-2">
            <Button variant="outline" size="sm">
              <Download className="w-4 h-4 mr-1" />
              Télécharger PDF
            </Button>
            <Button variant="outline" size="sm">
              <Share2 className="w-4 h-4 mr-1" />
              Partager
            </Button>
          </div>
        </div>

        <div className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Cachet de performance (€)
              </label>
              <input
                type="number"
                value={contractTerms?.performance_fee}
                onChange={(e) => setContractTerms(prev => ({ ...prev, performance_fee: e?.target?.value }))}
                placeholder="2500"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Conditions de paiement
              </label>
              <select
                value={contractTerms?.payment_terms}
                onChange={(e) => setContractTerms(prev => ({ ...prev, payment_terms: e?.target?.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="50% advance">50% acompte, 50% après prestation</option>
                <option value="100% advance">100% à la signature</option>
                <option value="30/70">30% acompte, 70% après prestation</option>
                <option value="full_after">100% après prestation</option>
              </select>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Exigences techniques
            </label>
            <textarea
              value={contractTerms?.technical_requirements}
              onChange={(e) => setContractTerms(prev => ({ ...prev, technical_requirements: e?.target?.value }))}
              rows={3}
              placeholder="Spécifiez les besoins en matériel audio, éclairage, espace scène..."
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Politique d'annulation
              </label>
              <select
                value={contractTerms?.cancellation_policy}
                onChange={(e) => setContractTerms(prev => ({ ...prev, cancellation_policy: e?.target?.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="48h">48h avant - remboursement intégral</option>
                <option value="7days">7 jours avant - remboursement intégral</option>
                <option value="14days">14 jours avant - remboursement intégral</option>
                <option value="30days">30 jours avant - remboursement intégral</option>
              </select>
            </div>

            <div className="space-y-3">
              <label className="block text-sm font-medium text-gray-700">
                Options supplémentaires
              </label>
              <div className="space-y-2">
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={contractTerms?.travel_expenses}
                    onChange={(e) => setContractTerms(prev => ({ ...prev, travel_expenses: e?.target?.checked }))}
                    className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  />
                  <span className="ml-2 text-sm text-gray-700">Frais de déplacement inclus</span>
                </label>
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={contractTerms?.equipment_provided}
                    onChange={(e) => setContractTerms(prev => ({ ...prev, equipment_provided: e?.target?.checked }))}
                    className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  />
                  <span className="ml-2 text-sm text-gray-700">Matériel fourni par l'organisateur</span>
                </label>
              </div>
            </div>
          </div>
        </div>

        <div className="mt-6 pt-6 border-t border-gray-200">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <Shield className="w-5 h-5 text-green-500" />
              <span className="text-sm font-medium text-green-700">
                Signature électronique sécurisée
              </span>
            </div>
            <div className="flex space-x-3">
              <Button variant="outline">
                <Edit3 className="w-4 h-4 mr-2" />
                Modifier
              </Button>
              <Button onClick={onCreateContract}>
                <FileText className="w-4 h-4 mr-2" />
                Finaliser le Contrat
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  const renderTabContent = () => {
    switch (activeTab) {
      case 'negotiate':
        return renderNegotiationTab();
      case 'contract':
        return renderContractTab();
      case 'calendar':
        return (
          <div className="text-center py-12">
            <Calendar className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-gray-900 mb-2">
              Intégration Calendrier
            </h3>
            <p className="text-gray-600">
              Synchronisation avec Google Calendar, Outlook et autres applications à venir
            </p>
          </div>
        );
      case 'payment':
        return (
          <div className="text-center py-12">
            <Euro className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-gray-900 mb-2">
              Système de Paiement Intégré
            </h3>
            <p className="text-gray-600">
              Paiements sécurisés avec Stripe, virements SEPA et cryptomonnaies à venir
            </p>
          </div>
        );
      default:
        return renderNegotiationTab();
    }
  };

  return (
    <div className="h-full">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 px-6 py-4">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-xl font-semibold text-gray-900">
              Négociation & Booking Sécurisé
            </h2>
            <p className="text-sm text-gray-600">
              Négociez et finalisez vos contrats en toute sécurité
            </p>
          </div>
          {userSubscription === 'free' && (
            <div className="bg-orange-50 border border-orange-200 rounded-lg px-4 py-2">
              <span className="text-sm text-orange-800 font-medium">
                🔒 Fonctionnalité Premium
              </span>
            </div>
          )}
        </div>
      </div>

      {/* Navigation Tabs */}
      <div className="border-b border-gray-200 px-6">
        <nav className="flex space-x-8">
          {tabs?.map((tab) => (
            <button
              key={tab?.id}
              onClick={() => setActiveTab(tab?.id)}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === tab?.id
                  ? 'border-blue-500 text-blue-600' :'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              {tab?.label}
            </button>
          ))}
        </nav>
      </div>

      {/* Tab Content */}
      <div className="flex-1 overflow-y-auto p-6">
        {renderTabContent()}
      </div>
    </div>
  );
};

export default BookingIntegration;